import React, { memo, useCallback, useEffect, useRef, useState } from 'react'
import { Controller } from 'react-hook-form'
import { X } from 'lucide-react'
import { InformationCircleIcon } from '@heroicons/react/24/outline'

const isValidVideoFile = (file: File) =>
  ['video/mp4', 'video/webm', 'video/ogg', 'video/mov', 'video/quicktime'].includes(file.type)

const isValidFileSize = (file: File, maxSizeMb: number) =>
  file.size / (1024 * 1024) <= maxSizeMb

const getVideoDuration = (file: File): Promise<number> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.preload = 'metadata'
    video.src = URL.createObjectURL(file)
    video.onloadedmetadata = () => {
      URL.revokeObjectURL(video.src)
      resolve(video.duration)
    }
    video.onerror = reject
  })

interface Props {
  name: string
  control: any
  label?: string
  onVideoMeta?: (meta: {
    file: File | null
    duration: number | null
    size: number | null
  }) => void
  rules?: any
  className?: string
  tooltip?: {
    title?: string
    items: string[]
    position?: 'top' | 'bottom' | 'left' | 'right'
  }
  required?: boolean
  fileSizeLimit?: { max_size: number; message: string }
  setError: any
  clearErrors: any
  existingFile?: File | null
  existingPreview?: string
}

const VideoDropzone: React.FC<Props> = memo(
  ({
    name,
    control,
    label,
    onVideoMeta,
    rules,
    tooltip,
    required,
    className = 'h-full min-h-[220px]',
    setError,
    clearErrors,
    fileSizeLimit = { max_size: 500, message: '' },
    existingFile,
    existingPreview,
  }) => {
    const fileInputRef = useRef<HTMLInputElement>(null)
    const previewUrlRef = useRef<string | null>(null)

    const [isDragging, setIsDragging] = useState(false)
    const [, forceRerender] = useState(0)

    // Restore existing preview on mount
    useEffect(() => {
      if (existingPreview && existingFile && !previewUrlRef.current) {
        previewUrlRef.current = existingPreview
        forceRerender((x) => x + 1)
      }
    }, [existingPreview, existingFile])

    useEffect(() => {
      return () => {
        if (previewUrlRef.current && previewUrlRef.current.startsWith('blob:')) {
          URL.revokeObjectURL(previewUrlRef.current)
          previewUrlRef.current = null
        }
      }
    }, [])

    const tooltipPositionClass: Record<string, string> = {
      top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
      bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
      left: 'right-full top-1/2 -translate-y-1/2 mr-2',
      right: 'left-full top-1/2 -translate-y-1/2 ml-2',
    }

    const processFile = useCallback(
      async (file: File, onChange: (f: File | null) => void) => {
        clearErrors(name)

        if (!isValidVideoFile(file)) {
          setError(name, {
            type: 'manual',
            message: 'Only MP4 / WebM / OGG / MOV supported',
          })
          return
        }

        if (!isValidFileSize(file, fileSizeLimit.max_size)) {
          setError(name, {
            type: 'manual',
            message:
              fileSizeLimit.message ||
              `Max size ${fileSizeLimit.max_size}MB`,
          })
          return
        }

        const duration = await getVideoDuration(file)

        onChange(file)

        onVideoMeta?.({
          file,
          duration,
          size: file.size,
        })

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current)
        }

        previewUrlRef.current = URL.createObjectURL(file)
        forceRerender((x) => x + 1)
      },
      [
        clearErrors,
        fileSizeLimit.max_size,
        fileSizeLimit.message,
        name,
        onVideoMeta,
        setError,
      ]
    )

    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        render={({ field, fieldState }) => (
          <div>
            {/* ===== Label Row ===== */}
            <div className="flex items-center gap-1 mb-2">
              {label && (
                <label
                  className={`text-xs font-medium dark:text-gray-300`}
                  // ${
                  //   fieldState.error ? 'text-red-600' : 'text-gray-700'
                  // }
                >
                  {label}
                </label>
              )}
              {required && <span className="text-red-700">*</span>}

              {tooltip && (
                <div className="relative group">
                  <InformationCircleIcon className="w-4 h-4 text-slate-400 cursor-pointer" />
                  <div
                    className={`absolute ${
                      tooltipPositionClass[tooltip.position ?? 'top']
                    } w-64 rounded-md z-50 bg-white border p-3 text-xs shadow-lg
                    opacity-0 invisible group-hover:opacity-100 group-hover:visible dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 transition-opacity`}
                  >
                    {tooltip.title && (
                      <p className="font-semibold mb-1">
                        {tooltip.title}
                      </p>
                    )}
                    <ul className="space-y-1">
                      {tooltip.items.map((item, idx) => (
                        <li key={idx}>• {item}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* ===== Hidden Input ===== */}
            <input
              ref={fileInputRef}
              type="file"
              accept="video/mp4,video/webm,video/ogg,video/quicktime"
              className="hidden"
              onChange={(e) => {
                if (!e.target.files?.[0]) return
                processFile(e.target.files[0], field.onChange)
              }}
            />

            {/* ===== Dropzone ===== */}
            <div
              onClick={() => {
                if (!previewUrlRef.current) {
                  fileInputRef.current?.click()
                }
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragEnter={() => setIsDragging(true)}
              onDragLeave={() => setIsDragging(false)}
              onDrop={(e) => {
                e.preventDefault()
                setIsDragging(false)
                if (!e.dataTransfer.files[0]) return
                processFile(e.dataTransfer.files[0], field.onChange)
              }}
              className={`
                relative border-2 border-dashed rounded-lg transition
                flex items-center justify-center overflow-hidden
                w-full h-[300px]
                ${previewUrlRef.current ? 'cursor-default' : 'cursor-pointer'}
                ${
                  isDragging
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-300 hover:border-blue-400'
                }
                ${className}
              `}
            >
              {previewUrlRef.current && !fieldState.error ? (
        <div className="relative w-full h-full px-2 p-2">
  <div className="relative w-full h-full bg-black rounded-md flex items-center justify-center overflow-hidden">
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation()

        field.onChange(null)
        clearErrors(name)

        onVideoMeta?.({
          file: null,
          duration: null,
          size: null,
        })

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current)
          previewUrlRef.current = null
        }

        if (fileInputRef.current) {
          fileInputRef.current.value = ''
        }

        forceRerender((x) => x + 1)
      }}
      className="
        absolute top-2 right-2 z-10
        flex items-center justify-center
        w-8 h-8
        bg-white/90 hover:bg-white
        text-red-600
        rounded-full
        shadow-md border border-red-200
      "
    >
      <X size={16} />
    </button>

    {/* 🎥 Video */}
    <video
      controls
      className="max-w-full max-h-full object-contain"
      src={previewUrlRef.current}
      onClick={(e) => e.stopPropagation()}
    />
  </div>
</div>
              ) : (
                <div className="text-center text-gray-500">
                  <p className="font-medium">
                    Drag & drop video or click to browse
                  </p>
                  <p className="text-sm mt-1">
                    MP4, WebM, OGG, Mov
                  </p>

                  {fieldState.error && (
                    <p className="text-xs text-red-500 mt-2">
                      {fieldState.error.message}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      />
    )
  }
)

export default VideoDropzone
